import { ITEM_TYPES } from 'constants/itemType';
import { NotificationPriority, NotificationType } from 'hooks/useNotification';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import type { UserNotificationWithDetails } from 'hooks/useUserNotifications';
import { HELENE_ADVISORIES } from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/historicalHurricanesHeleneData';
import type {
  MemphisAssetAtRisk,
  MemphisRegionalThreats,
} from './memphisThreatNotification';

/** Stable id for the hardcoded DHS Region 4 tropical-storm inbox item. */
export const DHS_REGION4_THREAT_NOTIFICATION_ID =
  'demo-dhs-region4-tropical-storm';

export const DHS_REGION4_ASSETS_LAYER_ID = 'dhs-region4-assets';
export const DHS_REGION4_ASSETS_LAYER_TITLE = 'DHS Region 4 assets';

const DHS_REGION4_THREAT_SENT_AT = '2024-09-24T15:30:00.000Z';

const TS_ADVISORY =
  HELENE_ADVISORIES.find((a) => a.category === 'TS') ?? HELENE_ADVISORIES[1];

export const DHS_REGION4_THREAT_PROVENANCE =
  '7 Assets to be Impacted by Tropical Storm per NHC Tropical Storms & Hurricanes';

export const DHS_REGION4_THRESHOLD_DESCRIPTION =
  'Tropical storm category threshold exceeded — Helene forecast cone contains DHS resources';

export const DHS_REGION4_AUTOMATION_RULE_NAME =
  'DHS Region 4 — tropical storm forecast cone';
export const DHS_REGION4_FLOOD_RULE_NAME = 'DHS Region 4 — flood';
export const DHS_REGION4_WILDFIRE_RULE_NAME = 'DHS Region 4 — wildfire';
export const DHS_REGION4_INFRASTRUCTURE_RULE_NAME =
  'DHS Region 4 — infrastructure';

export const DHS_THREAT_TO_RULE_NAME: Record<string, string> = {
  'Tropical Storm': DHS_REGION4_AUTOMATION_RULE_NAME,
  Flood: DHS_REGION4_FLOOD_RULE_NAME,
  Wildfire: DHS_REGION4_WILDFIRE_RULE_NAME,
  Infrastructure: DHS_REGION4_INFRASTRUCTURE_RULE_NAME,
};

export const DHS_REGION4_EVENT_SUMMARY =
  'Hurricane Helene is forecast to bring tropical-storm-force winds across FEMA Region IV. DHS facilities will be impacted by hurricane-force winds per the 24 September 2024 advisory.';

export const DHS_REGION4_LINKED_EVENT_NAME =
  'Tropical Storm — DHS 7 assets in Tropical Storm path';

export const DHS_REGION4_RESOURCES_COMMITTED =
  '7 resources are within the tropical storm cone';

export const DHS_REGION4_ORG_EVENT_DEMO_KEY = 'dhs-region4-cone';

export const DHS_REGION4_RESOURCE_NOTES_PREFIX = 'dhs-region4:';

export const DHS_REGION4_SYMBOLOGY = 'facility';

export const DHS_RESPONSE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'dhs-checklist-accountability',
    kind: 'send_notification',
    label: 'Send accountability check notification',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate:
        'Accountability check: DHS Region 4 facilities in {eventName} cone',
      bodyTemplate:
        'Confirm personnel accountability for DHS facilities inside the {eventName} forecast cone. Report status in PRATUS.',
      emailSubject:
        'Accountability check: DHS Region 4 facilities — {eventName}',
      emailMessage:
        'Confirm personnel accountability for DHS facilities inside the {eventName} forecast cone. Review Courses of Action and assets at risk in PRATUS.',
      appMessage:
        'Accountability check: {eventName} — confirm personnel status for DHS Region 4 facilities.',
    },
  },
  {
    id: 'dhs-checklist-community-lifelines',
    kind: 'org_action',
    label: 'Assess stability of community lifelines',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'dhs-checklist-mission-essential',
    kind: 'org_action',
    label: 'Assess stability of mission essential functions',
    orgAction: { status: 'Planned', poc: null },
  },
];

export const DHS_RESPONSE_CHECKLIST_ORG_ACTION_LABELS =
  DHS_RESPONSE_CHECKLIST_ITEMS.filter((item) => item.kind === 'org_action').map(
    (item) => item.label
  );

export function isDhsRegion4OrgActionName(
  name: string | null | undefined
): boolean {
  return !!name && DHS_RESPONSE_CHECKLIST_ORG_ACTION_LABELS.includes(name);
}

export const DHS_FLOOD_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'dhs-flood-barriers',
    kind: 'org_action',
    label: 'Deploy flood barriers at facility entry points',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'dhs-flood-elevate-systems',
    kind: 'org_action',
    label: 'Elevate or isolate critical systems above flood stage',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'dhs-flood-accountability',
    kind: 'send_notification',
    label: 'Send flood-stage accountability check',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'warning',
      titleTemplate: 'Flood accountability check — {eventName}',
      bodyTemplate:
        'Confirm personnel accountability and facility access as flood risk rises. Report status in PRATUS.',
      emailSubject: 'Flood accountability check — {eventName}',
      emailMessage:
        'Confirm personnel accountability and isolate critical systems. Review Courses of Action in PRATUS.',
      appMessage:
        'Flood accountability check: confirm personnel status and access.',
    },
  },
];

export const DHS_WILDFIRE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'dhs-wildfire-defend',
    kind: 'org_action',
    label: 'Prepare defend-in-place and staff evac routes',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'dhs-wildfire-mef',
    kind: 'org_action',
    label: 'Assess mission essential functions under smoke/heat',
    orgAction: { status: 'Planned', poc: null },
  },
];

export const DHS_INFRASTRUCTURE_CHECKLIST_ITEMS: OrgEventChecklistItemDef[] = [
  {
    id: 'dhs-infra-backup-power',
    kind: 'org_action',
    label: 'Verify backup power and comms failover',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'dhs-infra-mef',
    kind: 'org_action',
    label: 'Assess stability of mission essential functions',
    orgAction: { status: 'Planned', poc: null },
  },
  {
    id: 'dhs-infra-ops-notify',
    kind: 'send_notification',
    label: 'Notify ops of infrastructure disruption risk',
    notify: {
      notificationTypes: [NotificationType.IN_APP, NotificationType.EMAIL],
      priority: 'watch',
      titleTemplate: 'Infrastructure watch — {eventName}',
      bodyTemplate:
        'Infrastructure disruption may affect DHS facility operations. Confirm backup power and MEF status in PRATUS.',
      emailSubject: 'Infrastructure watch — {eventName}',
      emailMessage:
        'Review backup power, comms failover, and mission essential functions in PRATUS.',
      appMessage: 'Infrastructure watch: confirm backup power and MEF status.',
    },
  },
];

const TROPICAL_STORM_THREAT = 'Tropical Storm';
const FLOOD_THREAT = 'Flood';
const WILDFIRE_THREAT = 'Wildfire';
const INFRASTRUCTURE_THREAT = 'Infrastructure';

export function dhsChecklistsForThreats(
  threats: string[]
): Record<string, OrgEventChecklistItemDef[]> {
  const checklists: Record<string, OrgEventChecklistItemDef[]> = {};
  for (const threat of threats) {
    if (threat === TROPICAL_STORM_THREAT) {
      checklists[threat] = DHS_RESPONSE_CHECKLIST_ITEMS;
    } else if (threat === FLOOD_THREAT) {
      checklists[threat] = DHS_FLOOD_CHECKLIST_ITEMS;
    } else if (threat === WILDFIRE_THREAT) {
      checklists[threat] = DHS_WILDFIRE_CHECKLIST_ITEMS;
    } else if (threat === INFRASTRUCTURE_THREAT) {
      checklists[threat] = DHS_INFRASTRUCTURE_CHECKLIST_ITEMS;
    }
  }
  return checklists;
}

export function dhsChecklistsForAutomationRules(threats: string[]): {
  automationRules: string[];
  automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]>;
} {
  const threatChecklists = dhsChecklistsForThreats(threats);
  const automationRules: string[] = [];
  const automationRuleChecklists: Record<string, OrgEventChecklistItemDef[]> =
    {};
  for (const threat of threats) {
    const ruleName = DHS_THREAT_TO_RULE_NAME[threat];
    if (!ruleName) continue;
    if (!automationRules.includes(ruleName)) automationRules.push(ruleName);
    const items = threatChecklists[threat];
    if (items) automationRuleChecklists[ruleName] = items;
  }
  return { automationRules, automationRuleChecklists };
}

export type DhsRegion4Facility = {
  id: string;
  name: string;
  city: string;
  state: string;
  lon: number;
  lat: number;
};

export const DHS_REGION4_FACILITIES: DhsRegion4Facility[] = [
  {
    id: 'dhs-fema-r4-atlanta',
    name: 'FEMA Region IV Regional Office',
    city: 'Atlanta',
    state: 'GA',
    lon: -84.3074,
    lat: 33.8806,
  },
  {
    id: 'dhs-uscg-panama-city',
    name: 'USCG Station Panama City',
    city: 'Panama City',
    state: 'FL',
    lon: -85.6602,
    lat: 30.1588,
  },
  {
    id: 'dhs-uscg-st-petersburg',
    name: 'USCG Sector St. Petersburg',
    city: 'St. Petersburg',
    state: 'FL',
    lon: -82.6336,
    lat: 27.7606,
  },
  {
    id: 'dhs-cbp-jacksonville',
    name: 'CBP Port of Jacksonville',
    city: 'Jacksonville',
    state: 'FL',
    lon: -81.465,
    lat: 30.398,
  },
  {
    id: 'dhs-cbp-savannah',
    name: 'CBP Port of Savannah',
    city: 'Savannah',
    state: 'GA',
    lon: -81.151,
    lat: 32.125,
  },
  {
    id: 'dhs-uscg-charleston',
    name: 'USCG Sector Charleston',
    city: 'Charleston',
    state: 'SC',
    lon: -79.9311,
    lat: 32.7765,
  },
  {
    id: 'dhs-uscg-north-carolina',
    name: 'USCG Sector North Carolina',
    city: 'Wilmington',
    state: 'NC',
    lon: -77.9447,
    lat: 34.2257,
  },
];

export type DhsAssetsTabSeed = DhsRegion4Facility & {
  threats: string[];
  inHeleneCone?: boolean;
};

const HELENE_CONE_EXTRA_THREATS: Record<string, string[]> = {
  'dhs-fema-r4-atlanta': [INFRASTRUCTURE_THREAT],
  'dhs-uscg-panama-city': [FLOOD_THREAT],
  'dhs-uscg-st-petersburg': [FLOOD_THREAT],
  'dhs-cbp-jacksonville': [FLOOD_THREAT],
  'dhs-cbp-savannah': [FLOOD_THREAT],
  'dhs-uscg-charleston': [FLOOD_THREAT],
  'dhs-uscg-north-carolina': [FLOOD_THREAT],
};

export const DHS_ASSETS_TAB_EXTRA_FACILITIES: DhsAssetsTabSeed[] = [
  {
    id: 'dhs-tsa-atl',
    name: 'TSA Hartsfield-Jackson Atlanta',
    city: 'Atlanta',
    state: 'GA',
    lon: -84.4277,
    lat: 33.6407,
    threats: [INFRASTRUCTURE_THREAT],
  },
  {
    id: 'dhs-ice-atlanta',
    name: 'ICE Atlanta Field Office',
    city: 'Atlanta',
    state: 'GA',
    lon: -84.3904,
    lat: 33.7537,
    threats: [INFRASTRUCTURE_THREAT],
  },
  {
    id: 'dhs-fema-r4-distribution',
    name: 'FEMA Region IV Distribution Center',
    city: 'Atlanta',
    state: 'GA',
    lon: -84.42,
    lat: 33.65,
    threats: [WILDFIRE_THREAT, INFRASTRUCTURE_THREAT],
  },
  {
    id: 'dhs-cbp-miami',
    name: 'CBP Port of Miami',
    city: 'Miami',
    state: 'FL',
    lon: -80.193,
    lat: 25.774,
    threats: [FLOOD_THREAT],
  },
  {
    id: 'dhs-uscg-miami',
    name: 'USCG Sector Miami',
    city: 'Miami',
    state: 'FL',
    lon: -80.189,
    lat: 25.7743,
    threats: [FLOOD_THREAT],
  },
  {
    id: 'dhs-cbp-tampa',
    name: 'CBP Port of Tampa',
    city: 'Tampa',
    state: 'FL',
    lon: -82.4572,
    lat: 27.9506,
    threats: [FLOOD_THREAT],
  },
  {
    id: 'dhs-uscg-mobile',
    name: 'USCG Sector Mobile',
    city: 'Mobile',
    state: 'AL',
    lon: -88.0399,
    lat: 30.6954,
    threats: [FLOOD_THREAT],
  },
  {
    id: 'dhs-cbp-mobile',
    name: 'CBP Port of Mobile',
    city: 'Mobile',
    state: 'AL',
    lon: -88.043,
    lat: 30.695,
    threats: [FLOOD_THREAT],
  },
  {
    id: 'dhs-tsa-mco',
    name: 'TSA Orlando International',
    city: 'Orlando',
    state: 'FL',
    lon: -81.3081,
    lat: 28.4312,
    threats: [INFRASTRUCTURE_THREAT],
  },
  {
    id: 'dhs-ice-new-orleans',
    name: 'ICE New Orleans Field Office',
    city: 'New Orleans',
    state: 'LA',
    lon: -90.0715,
    lat: 29.9511,
    threats: [FLOOD_THREAT],
  },
  {
    id: 'dhs-fema-jfo-tallahassee',
    name: 'FEMA Joint Field Office Tallahassee',
    city: 'Tallahassee',
    state: 'FL',
    lon: -84.2807,
    lat: 30.4383,
    threats: [FLOOD_THREAT],
  },
  {
    id: 'dhs-cbp-charleston',
    name: 'CBP Port of Charleston',
    city: 'Charleston',
    state: 'SC',
    lon: -79.924,
    lat: 32.781,
    threats: [FLOOD_THREAT],
  },
  {
    id: 'dhs-uscis-atlanta',
    name: 'USCIS Atlanta Field Office',
    city: 'Atlanta',
    state: 'GA',
    lon: -84.383,
    lat: 33.786,
    threats: [INFRASTRUCTURE_THREAT],
  },
];

export const DHS_ASSETS_TAB_SEEDS: DhsAssetsTabSeed[] = [
  ...DHS_REGION4_FACILITIES.map((facility) => ({
    ...facility,
    inHeleneCone: true,
    threats: [
      TROPICAL_STORM_THREAT,
      ...(HELENE_CONE_EXTRA_THREATS[facility.id] ?? []),
    ],
  })),
  ...DHS_ASSETS_TAB_EXTRA_FACILITIES,
];

export function formatDhsFacilityLabel(facility: DhsRegion4Facility): string {
  return `${facility.name} (${facility.city}, ${facility.state})`;
}

export function dhsRegion4ResourceNotesMarker(facilityId: string): string {
  return `${DHS_REGION4_RESOURCE_NOTES_PREFIX}${facilityId}`;
}

export function dhsFacilityIdFromResourceNotes(
  notes: string | null | undefined
): string | undefined {
  if (!notes?.startsWith(DHS_REGION4_RESOURCE_NOTES_PREFIX)) return undefined;
  const id = notes.slice(DHS_REGION4_RESOURCE_NOTES_PREFIX.length);
  return id || undefined;
}

export function isDhsRegion4ResourceSet(resource: {
  notes?: string | null;
}): boolean {
  return !!resource.notes?.startsWith(DHS_REGION4_RESOURCE_NOTES_PREFIX);
}

function buildAssetFromFacility(
  facility: DhsRegion4Facility
): MemphisAssetAtRisk {
  return {
    id: facility.id,
    resource: formatDhsFacilityLabel(facility),
    risk:
      `${formatDhsFacilityLabel(facility)} sits inside the Helene ` +
      `forecast cone while the system is at Tropical Storm intensity ` +
      `(${TS_ADVISORY.maxWindMph} mph). Confirm site readiness and report status.`,
    location: [facility.lon, facility.lat],
    operationalStatus: 'Partially Operational',
  };
}

export function getDhsRegionalThreats(): MemphisRegionalThreats {
  return {
    region: 'Region 4 — Helene forecast cone',
    description:
      'Tropical Storm Helene places the following DHS resources inside the National Hurricane Center forecast cone at elevated risk:',
    threats: DHS_REGION4_FACILITIES.map(buildAssetFromFacility),
    responseChecklist: DHS_RESPONSE_CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
    })),
  };
}

export function isHardcodedDhsRegion4ThreatNotification(
  notificationId: string | null | undefined
): boolean {
  return notificationId === DHS_REGION4_THREAT_NOTIFICATION_ID;
}

export function getDhsRegion4ThreatNotification(
  groupId: string
): UserNotificationWithDetails {
  return {
    userGuid: 'system',
    notificationId: DHS_REGION4_THREAT_NOTIFICATION_ID,
    groupId,
    archived: false,
    acknowledged: false,
    canArchive: false,
    createdAt: DHS_REGION4_THREAT_SENT_AT,
    acknowledgedAt: null,
    archivedAt: null,
    priority: NotificationPriority.WARNING,
    status: 'sent',
    acknowledgementRequired: false,
    sourceItemId: '',
    sourceItemType: ITEM_TYPES.Sitrep,
    sentAt: DHS_REGION4_THREAT_SENT_AT,
    scheduledDate: null,
    incidentId: null,
    aors: [],
    appMessage:
      'Likely impact to DHS resources due to Tropical Storm in FEMA Region IV.',
    emailSubject: null,
    emailMessage: null,
    smsMessage: null,
    teamsMessage: null,
    sendEmail: null,
    sendSms: null,
    sendTeams: null,
    actionType: null,
    createdByName: DHS_REGION4_THREAT_PROVENANCE,
  };
}
